import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Stack } from '@sureco/design-system'
import {
  DatePicker,
  Input,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import type { Dayjs } from 'dayjs'
import PageWrapper from '../../../PageWrapper'
import Page from 'layouts/Page'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import {
  getTerminationRequests,
  GetTerminationRequestsParams,
  reconcileFailedTerminationRequests,
  waitForReconcileFailedJob,
} from '../api/termination-tracking-enrollment-platform-api-client'
import {
  TerminationRequestTabCountsDto,
  TerminationRequestTrackingItemDto,
  TerminationStageDto,
} from '../api/dto/termination-request-tracking.dto'
import ClientSelect from 'components/ClientSelect'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { isProduction } from 'modules/app-config/environment'
import FlexspringSourceFileModal from '../components/FlexspringSourceFileModal'
import TerminationSourceFileModal from '../components/TerminationSourceFileModal'
import { actorLabel, actorLinkId } from './termination-tracking-actor'
import { hasSourceFilePreview } from './termination-tracking-source'

const { Text, Link } = Typography
const { Search } = Input
const { RangePicker } = DatePicker

type DateRangeValue = [Dayjs | null, Dayjs | null] | null

const zendeskDomain = isProduction()
  ? 'sureco.zendesk.com'
  : 'surecodev.zendesk.com'

const zendeskTicketUrl = (ticketId: number) =>
  `https://${zendeskDomain}/agent/tickets/${ticketId}`

const employeeCoveragePath = (employeePublicKey: string) =>
  `/employees/${employeePublicKey}/coverage`

const EmployeeLink = ({
  employeePublicKey,
  children,
  secondary,
}: {
  employeePublicKey: string
  children: React.ReactNode
  secondary?: boolean
}) => {
  if (!employeePublicKey) {
    return <Text type={secondary ? 'secondary' : undefined}>{children}</Text>
  }

  return (
    <Tooltip title="Open in Our people">
      <Link
        href={employeeCoveragePath(employeePublicKey)}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: secondary ? 'rgba(0, 0, 0, 0.45)' : '#003146',
        }}
      >
        {children}
      </Link>
    </Tooltip>
  )
}

type TabKey = 'IN_FLIGHT' | 'ON_HOLD' | 'AWAITING_OPS' | 'COMPLETED' | 'FAILED'
type StageFilterValue = '' | 'DONE' | 'PENDING' | 'FAILED'

const STAGE_FILTER_OPTIONS: { value: StageFilterValue; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'DONE', label: 'Done' },
  { value: 'PENDING', label: 'In progress' },
  { value: 'FAILED', label: 'Failed' },
]

const STAGE_ORDER = ['FUNDING', 'VCN', 'STATUS'] as const

const STAGE_LABELS: Record<(typeof STAGE_ORDER)[number], string> = {
  FUNDING: 'Funding',
  VCN: 'VCN',
  STATUS: 'Status',
}

const humanize = (value?: string | null) => {
  if (!value) return '—'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const stageColor = (
  status?: string,
  stageKey?: (typeof STAGE_ORDER)[number],
  detail?: string | null
) => {
  if (
    stageKey === 'FUNDING' &&
    status === 'DONE' &&
    detail === 'no active benefits'
  ) {
    return 'gold'
  }

  switch (status) {
    case 'DONE':
      return 'green'
    case 'FAILED':
      return 'red'
    case 'NA':
      return 'default'
    default:
      return 'blue'
  }
}

const requestStatusColor = (status?: string) => {
  switch (status) {
    case 'COMPLETED':
    case 'ALREADY_TERMINATED':
      return 'green'
    case 'FAILED':
      return 'red'
    case 'AWAITING_OPS':
      return 'orange'
    default:
      return 'blue'
  }
}

const renderStageBar = (stages: Record<string, TerminationStageDto> = {}) => (
  <Stack direction="row" space={4} wrap="wrap">
    {STAGE_ORDER.map((key) => {
      const stage = stages[key]
      const status = stage?.status ?? 'PENDING'
      const label = `${STAGE_LABELS[key]}: ${humanize(status)}`
      return (
        <Tooltip key={key} title={stage?.detail || label}>
          <Tag
            color={stageColor(status, key, stage?.detail)}
            style={{ marginInlineEnd: 0 }}
          >
            {STAGE_LABELS[key]}
          </Tag>
        </Tooltip>
      )
    })}
  </Stack>
)

const emptyCounts: TerminationRequestTabCountsDto = {
  IN_FLIGHT: 0,
  ON_HOLD: 0,
  AWAITING_OPS: 0,
  COMPLETED: 0,
  FAILED: 0,
}

const TerminationTrackingDashboard = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('IN_FLIGHT')
  const [items, setItems] = useState<TerminationRequestTrackingItemDto[]>([])
  const [tabCounts, setTabCounts] =
    useState<TerminationRequestTabCountsDto>(emptyCounts)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [selectedClient, setSelectedClient] = useState<
    SimpleCustomerDto | undefined
  >()
  const [sourcePreview, setSourcePreview] =
    useState<TerminationRequestTrackingItemDto | null>(null)
  const [fundingStage, setFundingStage] = useState<StageFilterValue>('')
  const [vcnStage, setVcnStage] = useState<StageFilterValue>('')
  const [statusStage, setStatusStage] = useState<StageFilterValue>('')
  const [submitDateRange, setSubmitDateRange] = useState<DateRangeValue>(null)
  const [benefitEndDateRange, setBenefitEndDateRange] =
    useState<DateRangeValue>(null)
  const [reconcilingAll, setReconcilingAll] = useState(false)
  const { clients, error } = useCensusV2Clients()

  if (error) {
    message.error(`Error loading companies: ${error.message}`, 5)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params: GetTerminationRequestsParams = {
        page,
        limit: 25,
        clientPublicKey: selectedClient?.id || undefined,
        search: search || undefined,
        fundingStage: fundingStage || undefined,
        vcnStage: vcnStage || undefined,
        statusStage: statusStage || undefined,
        submitDateFrom:
          submitDateRange?.[0]?.startOf('day').toISOString() || undefined,
        submitDateTo:
          submitDateRange?.[1]?.endOf('day').toISOString() || undefined,
        benefitEndDateFrom:
          benefitEndDateRange?.[0]?.format('YYYY-MM-DD') || undefined,
        benefitEndDateTo:
          benefitEndDateRange?.[1]?.format('YYYY-MM-DD') || undefined,
      }
      params.state = activeTab
      const data = await getTerminationRequests(params)
      setItems(data.items)
      setTotal(data.total)
      setTabCounts(data.tabCounts ?? emptyCounts)
    } catch (err: any) {
      console.error(err)
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load termination requests'
      )
    } finally {
      setLoading(false)
    }
  }, [
    activeTab,
    page,
    search,
    selectedClient?.id,
    fundingStage,
    vcnStage,
    statusStage,
    submitDateRange,
    benefitEndDateRange,
  ])

  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    load()
  }, [load])

  const handleReconcileAllFailed = useCallback(async () => {
    setReconcilingAll(true)
    try {
      const { jobId } = await reconcileFailedTerminationRequests()
      const status = await waitForReconcileFailedJob(jobId)
      if (status.errorMessage) {
        message.error(status.errorMessage)
        return
      }
      const processed = status.output?.processed ?? 0
      const completed = status.output?.completed ?? 0
      if (processed === 0) {
        message.info('No failed requests to reconcile')
        return
      }
      message.success(
        `Reconciled ${completed} of ${processed} failed request${
          processed === 1 ? '' : 's'
        }`
      )
      await loadRef.current()
    } catch (err: any) {
      console.error(err)
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to start reconcile of failed requests'
      )
    } finally {
      setReconcilingAll(false)
    }
  }, [])

  const columns = useMemo(
    () => [
      {
        title: 'Employee',
        dataIndex: 'employeeName',
        key: 'employeeName',
        width: '16%',
        ellipsis: true,
        render: (_: unknown, row: TerminationRequestTrackingItemDto) => {
          if (row.employeeName) {
            return (
              <EmployeeLink employeePublicKey={row.employeePublicKey}>
                {row.employeeName}
              </EmployeeLink>
            )
          }
          return (
            <EmployeeLink employeePublicKey={row.employeePublicKey} secondary>
              Name unavailable
            </EmployeeLink>
          )
        },
      },
      {
        title: 'Emp ID',
        dataIndex: 'companyEmployeeId',
        key: 'companyEmployeeId',
        width: '10%',
        ellipsis: true,
        render: (
          v: string | null | undefined,
          row: TerminationRequestTrackingItemDto
        ) =>
          v ? (
            <EmployeeLink employeePublicKey={row.employeePublicKey}>
              {v}
            </EmployeeLink>
          ) : (
            '—'
          ),
      },
      {
        title: 'ZD Ticket',
        dataIndex: 'zendeskTicketId',
        key: 'zendeskTicketId',
        width: '8%',
        render: (ticketId: number | null | undefined) =>
          ticketId ? (
            <Link
              href={zendeskTicketUrl(ticketId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              #{ticketId}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        width: '10%',
        render: (v: string, row: TerminationRequestTrackingItemDto) => {
          const label = humanize(v)
          if (!hasSourceFilePreview(row)) {
            return label
          }
          return (
            <Tooltip title="View source file rows">
              <Link onClick={() => setSourcePreview(row)}>{label}</Link>
            </Tooltip>
          )
        },
      },
      {
        title: 'Actor',
        dataIndex: 'actor',
        key: 'actor',
        width: '12%',
        ellipsis: true,
        render: (_: unknown, row: TerminationRequestTrackingItemDto) => {
          const label = actorLabel(row)
          const requesterId = actorLinkId(row)
          if (!requesterId) {
            return label
          }
          return (
            <EmployeeLink employeePublicKey={requesterId}>{label}</EmployeeLink>
          )
        },
      },
      {
        title: 'Term date',
        dataIndex: 'terminationDate',
        key: 'terminationDate',
        width: '9%',
      },
      {
        title: 'Benefit end date',
        dataIndex: 'benefitEndDate',
        key: 'benefitEndDate',
        width: '10%',
        render: (v: string | null | undefined) => v || '—',
      },
      ...(activeTab === 'COMPLETED'
        ? [
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: '10%',
              render: (status: string) => (
                <Tag
                  color={requestStatusColor(status)}
                  style={{ marginInlineEnd: 0 }}
                >
                  {humanize(status)}
                </Tag>
              ),
            },
          ]
        : []),
      {
        title: 'Submit date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '11%',
        render: (v: string) =>
          v
            ? new Date(v).toLocaleString(undefined, {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })
            : '—',
      },
      {
        title: 'Stages',
        dataIndex: 'stages',
        key: 'stages',
        width: '16%',
        render: (stages: Record<string, TerminationStageDto>) =>
          renderStageBar(stages),
      },
    ],
    [activeTab]
  )

  const tabItems = [
    { key: 'IN_FLIGHT', label: `In flight (${tabCounts.IN_FLIGHT})` },
    { key: 'ON_HOLD', label: `On hold (${tabCounts.ON_HOLD ?? 0})` },
    { key: 'AWAITING_OPS', label: `Awaiting Ops (${tabCounts.AWAITING_OPS})` },
    { key: 'COMPLETED', label: `Completed (${tabCounts.COMPLETED})` },
    { key: 'FAILED', label: `Failed (${tabCounts.FAILED})` },
  ]

  return (
    <PageWrapper>
      <Page
        title="Termination Tracking"
        maxWidth="100%"
        action={
          activeTab === 'FAILED' ? (
            <Button
              variant="secondary"
              disabled={reconcilingAll || tabCounts.FAILED === 0}
              onClick={handleReconcileAllFailed}
            >
              {reconcilingAll
                ? 'Reconciling…'
                : `Reconcile failed (${tabCounts.FAILED})`}
            </Button>
          ) : undefined
        }
        breadcrumb={
          <BreadcrumbNav items={[{ title: 'Termination Tracking' }]} />
        }
      >
        <Stack direction="col" space={16} style={{ width: '100%' }}>
          <Stack
            direction="row"
            space={12}
            align="center"
            wrap="wrap"
            style={{ width: '100%', justifyContent: 'space-between' }}
          >
            <Text type="secondary" style={{ minWidth: 200 }}>
              Non-realtime snapshot
            </Text>
            <Stack direction="row" space={12} align="center">
              <Button variant="secondary" onClick={() => load()}>
                Refresh
              </Button>
            </Stack>
          </Stack>
          <Stack direction="col" space={12} style={{ width: '100%' }}>
            <Search
              allowClear
              placeholder="Search by name, emp ID, or employee key"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(value) => {
                setSearch(value.trim())
                setPage(1)
              }}
              style={{ width: 360 }}
            />
            <Stack
              direction="row"
              space={12}
              align="center"
              wrap="wrap"
              style={{ width: '100%' }}
            >
              <ClientSelect
                allCompaniesPlacholder={{
                  id: '',
                  companyName: 'All Companies',
                }}
                companies={clients.map(({ name, id }) => ({
                  companyName: name,
                  id: id,
                }))}
                defaultCompanyId={''}
                selectedCompanyId={selectedClient?.id}
                onChange={(value: string) => {
                  const c = clients.find((client) => client.id === value)
                  setSelectedClient(c)
                  setPage(1)
                }}
              />
              <Select
                value={fundingStage}
                options={STAGE_FILTER_OPTIONS.map((option) => ({
                  ...option,
                  label:
                    option.value === ''
                      ? 'Funding: All'
                      : `Funding: ${option.label}`,
                }))}
                onChange={(value: StageFilterValue) => {
                  setFundingStage(value)
                  setPage(1)
                }}
                style={{ width: 160 }}
              />
              <Select
                value={vcnStage}
                options={STAGE_FILTER_OPTIONS.map((option) => ({
                  ...option,
                  label:
                    option.value === '' ? 'VCN: All' : `VCN: ${option.label}`,
                }))}
                onChange={(value: StageFilterValue) => {
                  setVcnStage(value)
                  setPage(1)
                }}
                style={{ width: 150 }}
              />
              <Select
                value={statusStage}
                options={STAGE_FILTER_OPTIONS.map((option) => ({
                  ...option,
                  label:
                    option.value === ''
                      ? 'Status: All'
                      : `Status: ${option.label}`,
                }))}
                onChange={(value: StageFilterValue) => {
                  setStatusStage(value)
                  setPage(1)
                }}
                style={{ width: 170 }}
              />
              <RangePicker
                placeholder={['Submit from', 'Submit to']}
                value={submitDateRange}
                onChange={(dates) => {
                  setSubmitDateRange(dates)
                  setPage(1)
                }}
                format="YYYY-MM-DD"
                allowClear
              />
              <RangePicker
                placeholder={['Benefit end from', 'Benefit end to']}
                value={benefitEndDateRange}
                onChange={(dates) => {
                  setBenefitEndDateRange(dates)
                  setPage(1)
                }}
                format="YYYY-MM-DD"
                allowClear
              />
            </Stack>
          </Stack>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key as TabKey)
              setPage(1)
            }}
            items={tabItems}
          />
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={items}
            size="middle"
            tableLayout="fixed"
            style={{ width: '100%' }}
            pagination={{
              current: page,
              pageSize: 25,
              total,
              onChange: (p) => setPage(p),
              showTotal: (t) => `${t} requests`,
              showSizeChanger: false,
              style: {
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
              },
            }}
          />
          <TerminationSourceFileModal
            open={Boolean(
              sourcePreview &&
                (sourcePreview.terminationFilePublicKeys?.length ?? 0) > 0
            )}
            fileKeys={sourcePreview?.terminationFilePublicKeys ?? []}
            highlightEmployeeId={sourcePreview?.companyEmployeeId}
            onClose={() => setSourcePreview(null)}
          />
          <FlexspringSourceFileModal
            open={Boolean(
              sourcePreview &&
                sourcePreview.hasFlexspringSource &&
                (sourcePreview.terminationFilePublicKeys?.length ?? 0) === 0
            )}
            requestId={sourcePreview?.id}
            highlightEmployeeId={sourcePreview?.companyEmployeeId}
            onClose={() => setSourcePreview(null)}
          />
        </Stack>
      </Page>
    </PageWrapper>
  )
}

export default TerminationTrackingDashboard
